import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Link,
  Alert,
  AlertIcon,
  Container,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
  Card,
  CardBody,
  Heading,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckIcon, WarningIcon } from '@chakra-ui/icons';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import RotaryLogo from './RotaryLogo';
import ProductName from './ProductName';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    showPassword: false,
    showConfirmPassword: false,
  });
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState([]);
  const supabase = useSupabaseClient();
  const toast = useToast();

  // Extract token from URL on component mount
  useEffect(() => {
    const handlePasswordReset = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      if (type === 'recovery' && accessToken) {
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Error setting session:', error);
            setError('Invalid or expired reset link. Please request a new one.');
            setTimeout(() => navigate('/forgot-password'), 3000);
          }
        } catch (error) {
          console.error('Error handling reset:', error);
          setError('Invalid or expired reset link. Please request a new one.');
          setTimeout(() => navigate('/forgot-password'), 3000);
        }
      }
    };

    handlePasswordReset();
  }, [navigate, supabase.auth]);

  // Password validation checks
  const passwordChecks = {
    length: formData.password.length >= 8,
    hasLower: /[a-z]/.test(formData.password),
    hasUpper: /[A-Z]/.test(formData.password),
    hasNumber: /\d/.test(formData.password),
    hasSymbol: /[^a-zA-Z0-9]/.test(formData.password),
    matches: formData.password === formData.confirmPassword,
  };

  // Update form errors whenever password changes
  useEffect(() => {
    const errors = [];
    if (!passwordChecks.length) {
      errors.push("Password must be at least 8 characters long");
    }
    if (!passwordChecks.hasLower) {
      errors.push("Password must include at least one lowercase letter");
    }
    if (!passwordChecks.hasUpper) {
      errors.push("Password must include at least one uppercase letter");
    }
    if (!passwordChecks.hasNumber) {
      errors.push("Password must include at least one number");
    }
    if (!passwordChecks.hasSymbol) {
      errors.push("Password must include at least one symbol");
    }
    if (!passwordChecks.matches && formData.confirmPassword) {
      errors.push("Passwords don't match");
    }
    setFormErrors(errors);
  }, [formData.password, formData.confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formErrors.length > 0) {
      setError(formErrors[0]);
      return;
    }

    setLoading(true);

    try {
      const { data: { user }, error } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (error) {
        console.error('Password update error:', error);
        if (error.message.includes('same password')) {
          setError('New password must be different from your current password');
        } else if (error.message.includes('at least')) {
          setError('Password must be at least 8 characters long');
        } else if (error.message.includes('too weak') || error.message.includes('strong enough')) {
          setError('Password must include uppercase, lowercase, number, and symbol');
        } else if (error.message.includes('commonly used')) {
          setError('This password is too common or has been exposed in data breaches. Please choose a different password');
        } else if (error.message.includes('expired') || error.message.includes('missing')) {
          setError('Password reset link has expired. Please request a new one');
          setTimeout(() => navigate('/forgot-password'), 3000);
        } else {
          setError('Error resetting password: ' + error.message);
        }
        return;
      }

      toast({
        title: "Password updated",
        description: "Your password has been successfully reset. Please log in with your new password.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Sign out after successful password reset
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Password reset error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <Card>
        <CardBody>
          <VStack spacing="6">
            <RotaryLogo width="100px" height="100px" />
            <ProductName />
            <Heading size="lg">Set New Password</Heading>

            {error && (
              <Alert status="error" width="100%">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <VStack spacing={4} width="100%">
                <FormControl isRequired isInvalid={formErrors.length > 0}>
                  <FormLabel>New Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={formData.showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder="Enter new password"
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          formData.showPassword ? 'Hide password' : 'Show password'
                        }
                        icon={formData.showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() =>
                          setFormData({
                            ...formData,
                            showPassword: !formData.showPassword,
                          })
                        }
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Confirm Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={formData.showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          confirmPassword: e.target.value,
                        })
                      }
                      placeholder="Confirm new password"
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          formData.showConfirmPassword
                            ? 'Hide password'
                            : 'Show password'
                        }
                        icon={
                          formData.showConfirmPassword ? (
                            <ViewOffIcon />
                          ) : (
                            <ViewIcon />
                          )
                        }
                        onClick={() =>
                          setFormData({
                            ...formData,
                            showConfirmPassword: !formData.showConfirmPassword,
                          })
                        }
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Box w="100%" p={4} borderRadius="md" bg="gray.50">
                  <Text mb={2} fontWeight="medium">Password Requirements:</Text>
                  <List spacing={2}>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={passwordChecks.length ? CheckIcon : WarningIcon} 
                               color={passwordChecks.length ? 'green.500' : 'gray.300'} />
                      At least 8 characters long
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={passwordChecks.hasLower ? CheckIcon : WarningIcon}
                               color={passwordChecks.hasLower ? 'green.500' : 'gray.300'} />
                      One lowercase letter
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={passwordChecks.hasUpper ? CheckIcon : WarningIcon}
                               color={passwordChecks.hasUpper ? 'green.500' : 'gray.300'} />
                      One uppercase letter
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={passwordChecks.hasNumber ? CheckIcon : WarningIcon}
                               color={passwordChecks.hasNumber ? 'green.500' : 'gray.300'} />
                      One number
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={passwordChecks.hasSymbol ? CheckIcon : WarningIcon}
                               color={passwordChecks.hasSymbol ? 'green.500' : 'gray.300'} />
                      One symbol
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={passwordChecks.matches ? CheckIcon : WarningIcon}
                               color={passwordChecks.matches ? 'green.500' : 'gray.300'} />
                      Passwords match
                    </ListItem>
                  </List>
                  <Text mt={2} fontSize="sm" color="gray.600">
                    Note: Commonly used passwords or those exposed in data breaches will be rejected
                  </Text>
                </Box>

                <Button
                  type="submit"
                  colorScheme="blue"
                  width="100%"
                  isLoading={loading}
                >
                  Reset Password
                </Button>

                <Link color="blue.500" onClick={() => navigate('/login')}>
                  Back to Login
                </Link>
              </VStack>
            </form>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
};

export default ResetPassword;
